// Documents → e-Signatures: everything sent to be signed for this client —
// from a submission step or on its own — waiting first, then completed.
// "+ New request" is for a single document (an LON, a consent, anything);
// a submission's family forms are sent from the submission's own step.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPacketsWithProgress } from '../../lib/documents/packets';
import { listCycles } from '../../lib/paCycles';
import { normalizeLang } from '../../lib/documents/i18n';
import { isDmeOnly } from '../../lib/serviceModel';
import OpenLink from '../OpenLink';

const TYPE_LABELS = { private_intake: 'Private Home Care Intake', gapp_intake: 'Family forms', gapp_transfer: 'Transfer forms' };
const KIND_TITLE = { intake: 'Family forms — initial', renewal: 'Family forms — renewal', transfer: 'Family forms — transfer', lon: 'LON acknowledgment', other: 'Document' };
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function ClientESignatures({ client, refreshKey }) {
  const navigate = useNavigate();
  const [packets, setPackets] = useState(null);
  const [cycles, setCycles] = useState({});
  const suppliesOnly = isDmeOnly(client);

  useEffect(() => {
    (async () => {
      const [pk, cy] = await Promise.all([listPacketsWithProgress(client.id).catch(() => []), listCycles(client.id).catch(() => [])]);
      setPackets(pk); setCycles(Object.fromEntries(cy.map((c) => [c.id, c])));
    })();
  }, [client.id, refreshKey]);

  const waiting = (packets || []).filter((p) => p.status !== 'completed');
  const done = (packets || []).filter((p) => p.status === 'completed');
  const title = (p) => KIND_TITLE[p.request_kind] || p.title || TYPE_LABELS[p.packet_type] || p.packet_type;

  const Row = ({ p }) => {
    const cy = p.cycle_id ? cycles[p.cycle_id] : null;
    const isDone = p.status === 'completed';
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 16px', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {title(p)}
            {normalizeLang(p.language) === 'es' && <span style={chip}>ES</span>}
            <span style={{ ...pill, background: isDone ? 'var(--success-bg)' : p.status === 'draft' ? 'var(--bg2)' : 'var(--warn-bg)', color: isDone ? 'var(--success)' : p.status === 'draft' ? 'var(--text2)' : 'var(--warn)' }}>
              {isDone ? `completed ${fmt(p.completed_at)}` : p.status === 'draft' ? 'draft — not sent' : `waiting · ${p._signed} / ${p._total} signed`}
            </span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
            {isDone ? '' : `Created ${fmt(p.created_at)}`}{cy ? `${isDone ? '' : ' · '}part of ${cy.label}` : ''}{isDone && cy?.drive_folder_name ? ` · filed to ${cy.drive_folder_name}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {isDone && (cy?.drive_folder_id ? <OpenLink href={`https://drive.google.com/drive/folders/${cy.drive_folder_id}`} className="btn-ghost">Files</OpenLink> : p.final_pdf_url ? <OpenLink href={p.final_pdf_url} className="btn-ghost">PDF</OpenLink> : null)}
          {isDone && p.final_pdf_url && cy?.drive_folder_id && <OpenLink href={p.final_pdf_url} className="btn-ghost">Certificate</OpenLink>}
          <button className={`btn btn-sm ${isDone ? 'btn-ghost' : 'btn-outline'}`} onClick={() => navigate(`/packets/${p.id}`)}>{isDone ? 'Open' : 'Continue'}</button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>e-Signatures for {client.first_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Everything sent to be signed — from a submission step or on its own. Each document comes back as its own PDF.</div>
        </div>
        {!suppliesOnly && <button className="btn btn-primary btn-sm" onClick={() => navigate(`/packets/new?client=${client.id}&scope=doc`)}>+ New request</button>}
      </div>
      {packets === null ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div> : (
        <div style={card}>
          <div style={listHead}>Waiting</div>
          {waiting.length === 0 && <div style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text3)', borderTop: '1px solid var(--border)' }}>Nothing out for signature.</div>}
          {waiting.map((p) => <Row key={p.id} p={p} />)}
          <div style={{ ...listHead, borderTop: '1px solid var(--border)' }}>Completed</div>
          {done.length === 0 && <div style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text3)', borderTop: '1px solid var(--border)' }}>Nothing completed yet.</div>}
          {done.map((p) => <Row key={p.id} p={p} />)}
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--text3)' }}>The family forms for a submission are sent from <b>GAPP Submissions</b>, on the step that needs them. A Letter of Notification and other single documents start here.</div>
    </div>
  );
}

const card = { border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--card-bg)' };
const listHead = { padding: '10px 16px', fontSize: 11.5, fontWeight: 700, color: 'var(--text3)', letterSpacing: '.05em', textTransform: 'uppercase', background: 'var(--bg2)' };
const pill = { fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 10 };
const chip = { fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-border)' };
